import type {NetWorthQuery, NetWorthRole} from '@transactions/graphql';

type NetWorth = NetWorthQuery['netWorth'];

export type NetWorthChartPoint = {
  date: string;
  /** The shown groups summed. */
  total: number;
  /** Each shown group's net at this point. */
  byRole: Partial<Record<NetWorthRole, number>>;
};

export type NetWorthViewModel = {
  /** Where the history actually starts: the asked-for start, or later. */
  start: string;
  /** The total at the period's start. */
  startTotal: number | null;
  /** Each group's change from the period's start to today. */
  changeByRole: Map<NetWorthRole, {start: number; end: number}>;
  /** The total at the end of `date` (null before anything was recorded). */
  totalOn: (date: string) => number | null;
  /** The chart's points over the period, for the groups not hidden. */
  chartPoints: (hidden: NetWorthRole[]) => NetWorthChartPoint[];
};

/**
 * The page's view of an all-time result over one period — computed here,
 * so changing the period or the groups the chart shows is instant and never
 * refetches. Everything but the chart counts every group; hiding a group
 * only takes it off the chart.
 *
 * A history can't start before anything was recorded: a period reaching
 * further back starts at the first record instead.
 */
export const netWorthViewModel = (
  netWorth: NetWorth,
  {requestedStart}: {requestedStart: string},
): NetWorthViewModel => {
  const {history, roleSeries} = netWorth;
  const lastIndex = history.length - 1;
  // With nothing ever recorded, today's values are all that's known.
  const floor = history[0]?.date ?? netWorth.asOf;
  const start = requestedStart > floor ? requestedStart : floor;

  // The standing value at the start, then every later point through today.
  let startIndex = 0;
  for (let index = 0; index < history.length; index++) {
    if (history[index].date <= start) startIndex = index;
  }

  const chartPoints = (hidden: NetWorthRole[]): NetWorthChartPoint[] => {
    if (lastIndex < 0) return [];
    const shown = roleSeries.filter((series) => !hidden.includes(series.role));
    const pointAt = (index: number, date: string): NetWorthChartPoint => {
      const byRole: Partial<Record<NetWorthRole, number>> = {};
      let total = 0;
      for (const series of shown) {
        byRole[series.role] = series.totals[index];
        total += series.totals[index];
      }
      return {date, total, byRole};
    };
    return [
      pointAt(startIndex, start),
      ...history.flatMap((point, index) =>
        point.date > start ? [pointAt(index, point.date)] : [],
      ),
    ];
  };

  const totalOn = (date: string): number | null => {
    if (date < floor) return null;
    let index = -1;
    while (index + 1 < history.length && history[index + 1].date <= date) index++;
    return index < 0 ? null : history[index].total;
  };

  return {
    start,
    startTotal: lastIndex < 0 ? null : history[startIndex].total,
    changeByRole: new Map(
      roleSeries.map((series) => [
        series.role,
        {start: series.totals[startIndex] ?? 0, end: series.totals[lastIndex] ?? 0},
      ]),
    ),
    totalOn,
    chartPoints,
  };
};
